import React from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';

import Grid from '~/components/core/Atomic/Grid/Grid';
import useIsConfigurationFieldSupportedByCategories from '~/components/hooks/useIsConfigurationFieldSupportedByCategories';

import { getAllClaimRelatedRoles } from '../../../../communications/ContactUtils';
import ContactTextFieldFormik from '../../../../ContactTextFieldFormik';
import { useIncidentConfiguration } from '../../../../hooks/useIncidentConfiguration';
import ConfiguredFields, {
  cleanConfiguredFieldsFormikValuesBeforeSend,
  getBaseConfiguredFieldsValidations,
  getConfiguredFieldsEmptyFormikInitialValues,
  PreDefinedField,
  prepareConfiguredFields,
  preparePreDefinedFields,
} from '../../../../IncidentConfiguration/ConfiguredFields';
import { getAdditionalDataValidations } from '../../../../IncidentConfiguration/CustomFieldsContext';
import useOrganization from '../../../../OrganizationContext';

import { useStyles } from '../../../../../assets/styles';

const involvedPropertyBaseFields = {
  property_description: '',
  owner_contact_id: '',
  owner_contact: null,
  owner_contact_full_name: '',
  attorney_contact_id: '',
  attorney_contact: null,
  attorney_reference_number: '',
  insurer_contact_id: '',
  insurer_contact: null,
  insurer_reference_number: '',
  insurer_policy_number: '',
  damages: [],
  location: {},
  damages_description: '',
  is_stolen: null,
};

const getInvolvedPropertyInitialValues = (incidentConfiguration) => {
  const configuredFields = incidentConfiguration?.involved_parties?.involved_generic_property?.configured_fields || [];
  return {
    ...involvedPropertyBaseFields,
    configured_fields_values: getConfiguredFieldsEmptyFormikInitialValues(configuredFields),
  };
};

const getInvolvedPropertyConfiguredFields = (incidentConfiguration, additionalFieldOmitFn) =>
  prepareConfiguredFields(
    incidentConfiguration?.involved_parties?.involved_generic_property?.configured_fields,
    additionalFieldOmitFn
  );

const getInvolvedPropertyDefinedFields = (incidentConfiguration, additionalFieldOmitFn) => {
  const preDefinedFields = {
    ...incidentConfiguration?.involved_parties?.involved_property,
    ...incidentConfiguration?.involved_parties?.involved_generic_property,
  };

  return preparePreDefinedFields(preDefinedFields, additionalFieldOmitFn);
};

const getInvolvedPropertyValidationFields = (incidentConfiguration, additionalFieldOmitFn) => ({
  owner_contact_id: Yup.number().required('Required'),
  ...getAdditionalDataValidations(getInvolvedPropertyDefinedFields(incidentConfiguration, additionalFieldOmitFn)),
  configured_fields_values: Yup.object()
    .transform((value) => value ?? {})
    .shape({
      ...getBaseConfiguredFieldsValidations(
        getInvolvedPropertyConfiguredFields(incidentConfiguration, additionalFieldOmitFn)
      ),
    }),
});

const InvolvedPropertyFragment = ({ disableEditIdentity, showOnly }) => {
  const { classes } = useStyles();
  const { organizationContactRolesDict } = useOrganization();
  const { incidentConfiguration } = useIncidentConfiguration();
  const { isFieldSupportedByCategories } = useIsConfigurationFieldSupportedByCategories();
  const { values } = useFormikContext();
  const preDefinedFields = getInvolvedPropertyDefinedFields(incidentConfiguration, isFieldSupportedByCategories);
  const configuredFields = getInvolvedPropertyConfiguredFields(incidentConfiguration, isFieldSupportedByCategories);

  return (
    <Grid container spacing={1}>
      <PreDefinedField id="property_description" fields={preDefinedFields} inline gridXs={12} readOnly={showOnly} />
      <Grid item xs={12}>
        <ContactTextFieldFormik
          id="owner_contact"
          label="Owner"
          className={classes.textField}
          fullWidth
          acceptedRoles={getAllClaimRelatedRoles(organizationContactRolesDict).filter(
            (role) => !['insured', 'witness'].includes(role)
          )}
          contactSearchProps={{ newContactRole: 'claimant' }}
          disabled={disableEditIdentity}
          showOnly={showOnly}
          fixedSearchResults
        />
      </Grid>
      <PreDefinedField
        id="attorney_contact_id"
        fields={preDefinedFields}
        inline
        fixedSearchResults
        readOnly={showOnly}
      />
      <PreDefinedField id="attorney_reference_number" fields={preDefinedFields} inline readOnly={showOnly} />
      <PreDefinedField
        id="insurer_contact_id"
        fields={preDefinedFields}
        inline
        fixedSearchResults
        readOnly={showOnly}
      />
      <PreDefinedField id="insurer_reference_number" fields={preDefinedFields} inline readOnly={showOnly} />
      <PreDefinedField id="insurer_policy_number" fields={preDefinedFields} inline readOnly={showOnly} />
      <Grid item xs={6} />
      <PreDefinedField id="damages" fields={preDefinedFields} inline readOnly={showOnly} />
      <PreDefinedField id="is_stolen" fields={preDefinedFields} inline gridXs={12} readOnly={showOnly} />
      <PreDefinedField id="damages_description" fields={preDefinedFields} inline gridXs={12} readOnly={showOnly} />
      <Grid item xs={6} />
      <PreDefinedField id="location" fields={preDefinedFields} inline gridXs={12} readOnly={showOnly} />
      {configuredFields.length > 0 && (
        <ConfiguredFields values={values} customFields={configuredFields} inline readOnly={showOnly} />
      )}
    </Grid>
  );
};

InvolvedPropertyFragment.propTypes = {
  disableEditIdentity: PropTypes.bool,
  showOnly: PropTypes.bool,
};

export {
  cleanConfiguredFieldsFormikValuesBeforeSend,
  getInvolvedPropertyInitialValues,
  getInvolvedPropertyValidationFields,
  involvedPropertyBaseFields,
  InvolvedPropertyFragment,
};
